import { Drawers } from '@ubeya/shared-web/components';
import { employeeComplianceStatus } from '@ubeya/shared-web/utils/employee';
import useCompliances from '@ubeya/shared/hooks/account/useCompliances';
import useEmployeeCompliance from '@ubeya/shared/hooks/account/useEmployeeCompliance';
import useFields from '@ubeya/shared/hooks/account/useFields';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import LoaderArea from '@ubeya/shared-web/components/Loader/LoaderArea';
import { EmptyState } from 'shared/components';
import { RootContainer as BaseRootContainer, RootWrapper } from '../styled';
import EmployeeCompliancePositionSection from './EmployeeCompliancePositionSection';
import EditEmployeeFieldModal from 'compliances/components/EditEmployeeFieldModal';
import { complianceSelectedBranch, complianceSelectedPosition } from 'compliances/atoms/shared';

const RootContainer = styled(BaseRootContainer)`
  .drawer-label {
    padding: 16px 16px;
    letter-spacing: 0;
  }
`;

const EmployeeCompliance = ({ data: employeeData, employeeFields }) => {
  const { mappedPositions } = usePositions();
  const { mappedFields } = useFields();
  const { t } = useContext(ConfigContext);
  const selectedBranchId = useRecoilValue(complianceSelectedBranch);
  const selectedPositionId = useRecoilValue(complianceSelectedPosition);

  const [selectedField, setSelectedField] = useState(null);

  const { id: employeeId, firstName, lastName, fields: mappedEmployeeFields } = employeeData || {};

  const { isLoading, data: compliance, refetch: refetchEmployeeCompliance } = useEmployeeCompliance({
    employeeId
  });

  const { refetch: refecthGeneralCompliance } = useCompliances({
    positionId: selectedPositionId,
    branchIds: [selectedBranchId],
    enabled: false
  });

  const refetchCompliance = useCallback(() => {
    refetchEmployeeCompliance();

    //refetch the current compliance in case this is the relevant position id
    selectedField?.positionId === selectedPositionId && refecthGeneralCompliance?.();
  }, [refecthGeneralCompliance, refetchEmployeeCompliance, selectedField?.positionId, selectedPositionId]);

  const data = useMemo(
    () =>
      (compliance || []).map(({ positionId, isCompliant, minExpirationDate }) => {
        const position = mappedPositions[positionId];
        const positionComplianceFields = position?.compliance?.fieldsIds || [];

        const mappedComplianceFieldsEmployees = positionComplianceFields.reduce((all, fieldId) => {
          all[fieldId] = { ...mappedFields[fieldId], value: mappedEmployeeFields[`field-${fieldId}`]?.[0] };
          return all;
        }, {});

        const fieldsByCompliance = employeeFields
          .filter(({ id }) => positionComplianceFields.includes(id))
          .map((field) => ({ ...field, value: field.value[0] }));

        const status = employeeComplianceStatus({
          fields: fieldsByCompliance,
          minExpirationDate,
          positionComplianceFields,
          isCompliant
        });

        return {
          position,
          mappedComplianceFieldsEmployees,
          status,
          minExpirationDate
        };
      }),
    [compliance, employeeFields, mappedEmployeeFields, mappedFields, mappedPositions]
  );

  const employee = { id: employeeId, firstName, lastName };

  if (isLoading) {
    return <LoaderArea />;
  }

  return (
    <RootWrapper>
      {data?.length > 0 ? (
        <RootContainer>
          <Drawers>
            {(data || []).map((complianceRow, index) => (
              <EmployeeCompliancePositionSection
                key={index}
                compliance={complianceRow}
                mappedFields={mappedFields}
                mappedEmployeeFields={mappedEmployeeFields}
                setSelectedField={setSelectedField}
                employee={employee}
              />
            ))}
          </Drawers>

          {selectedField && (
            <EditEmployeeFieldModal
              refetchCompliance={refetchCompliance}
              data={selectedField}
              onClose={() => setSelectedField()}
            />
          )}
        </RootContainer>
      ) : (
        <EmptyState text={t('noEmployeeCompliance')} />
      )}
    </RootWrapper>
  );
};

export default React.memo(EmployeeCompliance);
